import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartPage = () => {
  const { user, getAuthHeader } = useAuth();
  const { cart, cartTotal, removeFromCart, loading } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const navigate = useNavigate();

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Please login to checkout');
      navigate('/login');
      return;
    }

    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        origin_url: window.location.origin,
        items: cart.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        }))
      }, {
        headers: getAuthHeader()
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to create checkout session');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center px-6"
        >
          <ShoppingBag className="w-20 h-20 text-[#E5D3B3]/30 mx-auto mb-6" />
          <h1 className="font-serif text-4xl text-[#F5F2EB] mb-4">Please Login</h1>
          <p className="text-[#A3988F] mb-8">Login to view your cart and checkout</p>
          <Link to="/login" className="btn-primary inline-flex items-center space-x-2" data-testid="cart-login-btn">
            <span>Login</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center px-6"
        >
          <ShoppingBag className="w-20 h-20 text-[#E5D3B3]/30 mx-auto mb-6" />
          <h1 className="font-serif text-4xl text-[#F5F2EB] mb-4">Your Cart is Empty</h1>
          <p className="text-[#A3988F] mb-8">Discover our handcrafted fashion collection</p>
          <Link to="/fashion" className="btn-primary inline-flex items-center space-x-2" data-testid="cart-shop-btn">
            <span>Shop Now</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      <section className="section-padding">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EB]">Shopping Cart</h1>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cart.items.map((item, index) => (
                <motion.div
                  key={item.product_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="luxury-card rounded-xl p-6 flex gap-6"
                >
                  <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={item.product?.image_url}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-xs tracking-[0.2em] uppercase text-[#8C7B6B] mb-1">
                          {item.product?.category}
                        </p>
                        <h3 className="font-serif text-lg text-[#F5F2EB]">{item.product?.name}</h3>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="text-[#A3988F] hover:text-[#FAD6C6] transition-colors"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-[#A3988F]">Qty: {item.quantity}</span>
                      </div>
                      <span className="text-lg font-semibold text-[#E5D3B3]">
                        ${(item.product?.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                    {item.customization_notes && (
                      <p className="mt-3 text-sm text-[#A3988F] bg-[#261E1D] p-3 rounded">
                        Note: {item.customization_notes}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="luxury-card rounded-xl p-8 sticky top-28"
              >
                <h2 className="font-serif text-2xl text-[#F5F2EB] mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-[#A3988F]">
                    <span>Subtotal</span>
                    <span>${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#A3988F]">
                    <span>Shipping</span>
                    <span>Free</span>
                  </div>
                  <div className="border-t border-[#E5D3B3]/10 pt-4 flex justify-between">
                    <span className="text-[#F5F2EB] font-semibold">Total</span>
                    <span className="text-2xl font-semibold text-[#E5D3B3]" data-testid="cart-total">
                      ${cartTotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="btn-primary w-full flex items-center justify-center space-x-2"
                  data-testid="checkout-btn"
                >
                  {checkoutLoading ? (
                    <div className="loader w-5 h-5" />
                  ) : (
                    <>
                      <span>Proceed to Checkout</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <Link
                  to="/fashion"
                  className="block text-center mt-4 text-[#A3988F] hover:text-[#E5D3B3] transition-colors text-sm"
                  data-testid="continue-shopping"
                >
                  Continue Shopping
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CartPage;
